import { Outlet, useNavigate, useBlocker } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { providerConfig } from '../../auth/providerConfig';
import AuthLayout from '../layouts/AuthLayout';
import { UserProvider } from '../../contexts/UserContext';
import { WorkerScheduleProvider } from '../../contexts/WorkerScheduleContext';
import { useEffect, useRef } from 'react';
import { GoogleMapsProvider } from '../../contexts/GoogleMapsContext';
import { WorkersViewProvider } from '../../contexts/WorkersContext';
import { EditModeGuardProvider, useEditModeGuard } from '../../contexts/EditModeGuardContext';

type AppState = {
  returnTo?: string;
};

const LEAVE_WITH_UNSAVED_CHANGES =
  'Du har osparade ändringar i resursplaneringen. Lämnar du sidan går de förlorade och din ' +
  'redigeringslåsning släpps, så att andra kan redigera. Vill du lämna sidan?';

/**
 * Warns before a route change would discard unsaved work.
 *
 * Lives here rather than in the page because react-router's useBlocker needs the data router,
 * which is created above this point. What it cannot see from here is whether there is
 * anything to warn about — the page registers that through EditModeGuardContext, so the
 * decision comes from real edit-mode state rather than from a localStorage flag that lock
 * acquisition, crash recovery and cleanup all write for their own reasons.
 *
 * Confirming only proceeds. Releasing the lock and clearing the session markers belongs to
 * the page's own unmount, which runs on every way out of the planner rather than just this
 * one, and doing it here as well would fire a second release against a lock already gone.
 */
const NavigationTracker = () => {
  const { shouldWarnBeforeLeaving } = useEditModeGuard();

  // Set once the user has said yes, so the same navigation is not blocked again on its way
  // through. Cleared when the blocker goes idle.
  const confirmedLeaveRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (confirmedLeaveRef.current) return false;

    return (
      currentLocation.pathname.includes('/resource-planning') &&
      !nextLocation.pathname.includes('/resource-planning') &&
      shouldWarnBeforeLeaving()
    );
  });

  // The destination already asked about, so a re-render while blocked does not reopen the
  // dialog on top of itself.
  const promptedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      promptedForRef.current = null;
      confirmedLeaveRef.current = false;
      return;
    }

    const destination = blocker.location.pathname;
    if (promptedForRef.current === destination) return;
    promptedForRef.current = destination;

    if (window.confirm(LEAVE_WITH_UNSAVED_CHANGES)) {
      confirmedLeaveRef.current = true;
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  return null; // This component doesn't render anything
};

export const Auth0ProviderWithRedirectCallback = () => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState: AppState | undefined): void => {
    navigate((appState && appState.returnTo) || window.location.pathname);
  };

  return (
    <Auth0Provider onRedirectCallback={onRedirectCallback} {...providerConfig}>
      <AuthLayout>
        <UserProvider>
          <WorkerScheduleProvider>
            <WorkersViewProvider>
              <EditModeGuardProvider>
                <NavigationTracker />
                <GoogleMapsProvider>
                  <Outlet />
                </GoogleMapsProvider>
              </EditModeGuardProvider>
            </WorkersViewProvider>
          </WorkerScheduleProvider>
        </UserProvider>
      </AuthLayout>
    </Auth0Provider>
  );
};
